/**
 * SQL Statement Parser Utility
 * Parses SQL text into individual statements and identifies which statement the cursor is on
 */

/**
 * Parse SQL text into individual statements
 * @param {string} sqlText - The full SQL text
 * @returns {Array} Array of statement objects with start and end positions
 */
export const parseSqlStatements = (sqlText) => {
  if (!sqlText || sqlText.trim() === '') {
    return [];
  }

  const statements = [];
  let currentStatement = '';
  let statementStart = 0;
  let inString = false;
  let stringChar = null;
  let inComment = false;
  let commentType = null; // 'line' or 'block'

  for (let i = 0; i < sqlText.length; i++) {
    const char = sqlText[i];
    const nextChar = sqlText[i + 1];
    const prevChar = sqlText[i - 1];

    // Handle comments
    if (!inString) {
      // Start of line comment
      if (char === '-' && nextChar === '-' && !inComment) {
        inComment = true;
        commentType = 'line';
        currentStatement += char;
        continue;
      }
      // Start of block comment
      if (char === '/' && nextChar === '*' && !inComment) {
        inComment = true;
        commentType = 'block';
        currentStatement += char;
        continue;
      }
      // End of line comment
      if (inComment && commentType === 'line' && char === '\n') {
        inComment = false;
        commentType = null;
        currentStatement += char;
        continue;
      }
      // End of block comment
      if (inComment && commentType === 'block' && char === '*' && nextChar === '/') {
        currentStatement += char;
        i++; // Skip the '/'
        currentStatement += sqlText[i];
        inComment = false;
        commentType = null;
        continue;
      }
    }

    // If in comment, just add the character and continue
    if (inComment) {
      currentStatement += char;
      continue;
    }

    // Handle strings
    if ((char === '"' || char === "'") && prevChar !== '\\') {
      if (!inString) {
        inString = true;
        stringChar = char;
      } else if (char === stringChar) {
        inString = false;
        stringChar = null;
      }
      currentStatement += char;
      continue;
    }

    // If in string, just add the character
    if (inString) {
      currentStatement += char;
      continue;
    }

    // Check for statement terminator (semicolon)
    if (char === ';') {
      currentStatement += char;
      const trimmedStatement = currentStatement.trim();
      
      if (trimmedStatement.length > 0) {
        statements.push({
          text: currentStatement,
          trimmedText: trimmedStatement,
          start: statementStart,
          end: i + 1,
        });
      }
      
      currentStatement = '';
      statementStart = i + 1;
    } else {
      currentStatement += char;
    }
  }

  // Add the last statement if it doesn't end with semicolon
  const trimmedStatement = currentStatement.trim();
  if (trimmedStatement.length > 0) {
    statements.push({
      text: currentStatement,
      trimmedText: trimmedStatement,
      start: statementStart,
      end: sqlText.length,
    });
  }

  return statements;
};

/**
 * Get the statement at a specific cursor position
 * @param {string} sqlText - The full SQL text
 * @param {number} cursorPosition - The cursor position
 * @returns {Object|null} The statement object at cursor position or null
 */
export const getStatementAtCursor = (sqlText, cursorPosition) => {
  const statements = parseSqlStatements(sqlText);
  
  for (const statement of statements) {
    if (cursorPosition >= statement.start && cursorPosition <= statement.end) {
      return statement;
    }
  }
  
  return statements.length > 0 ? statements[0] : null;
};

/**
 * Get all statements from SQL text
 * @param {string} sqlText - The full SQL text
 * @returns {Array} Array of trimmed statement texts
 */
export const getAllStatements = (sqlText) => {
  const statements = parseSqlStatements(sqlText);
  return statements.map(s => s.trimmedText);
};

